import {
  ClipboardDocumentListIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  LocalShipping as TruckIcon,
  Visibility as ViewIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CreateOrderModal from "./CreateOrderModal";
import { RootState } from "../../store";
import {
  clearSelectedOrder,
  fetchOrderById,
  fetchOrders,
  setLastViewedOrderId,
  clearLastViewedOrderId,
} from "./orderSlice";
import { USE_STUB_DATA } from "../../config/stubData";
import QRScanner from "../../components/scanner/QRScanner";
import QRCodePrintView from "../../components/QRCodePrintView";
import { Product, SubItem } from "./order";
import { OrderListShimmer } from "../../components/common/ShimmerLayouts";
import useShimmer from "../../hooks/useShimmer";
import { apiService } from "../../services/api";
import Breadcrumbs from "../../components/Breadcrumbs";
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import HeaderSection from "../../components/HeaderSection";
import { Item, Order } from "./order";
import { usePermission } from "../../components/common/PermissionGuard";
import PlaceOrderModal from "../site/PlaceOrderModal";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  selectSites,
  selectSiteError,
  selectSiteLoading,
  selectSelectedSite,
} from "../site/siteSlice";
import { Site } from "../site/site";
import {
  selectOrders,
  selectOrderError,
  selectOrderLoading,
  selectOrderPagination,
  selectLastViewedOrderId,
  selectSelectedOrder,
} from "./orderSlice";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import Pagination from "../../components/common/Pagination";
import { formatDate } from "../../utils/formatters";
import CompactOrderList from "./CompactOrderList";
import DetailedOrderList from "./DetailedOrderList";
import PageWrapper from "../../components/common/PageWrapper";

const Orders: React.FC = () => {
  const theme = useTheme();
  //const dispatch = useDispatch();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const { orders, loading } = useSelector((state: RootState) => state?.orders || []);
  const sites = useAppSelector(selectSites);
  // const selectedCustomer = useAppSelector(selectSelectedCustomer);
  const loading = useAppSelector(selectSiteLoading);
  const error = useAppSelector(selectSiteError);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const orders = useAppSelector(selectOrders);
  const selectedSite = useAppSelector(selectSelectedSite);
  const pagination = useAppSelector(selectOrderPagination);
  const lastViewedOrderId = useAppSelector(selectLastViewedOrderId);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [qrPrintOrder, setQrPrintOrder] = useState<Order | null>(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set<string>());
  const [expandedProducts, setExpandedProducts] = useState(new Set<string>());
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState<Order[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [viewMode, setViewMode] = useState<"compact" | "detailed">("detailed");
  const selectedOrderRef = useRef<HTMLDivElement | null>(null);

  // Debounced search functionality
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Track previous filter values to detect filter changes
  const prevFiltersRef = useRef({ search: debouncedSearchTerm, status: filterStatus, priority: filterPriority });
  const isInitialMountRef = useRef(true);
  
  // Initial load and filter changes - consolidated into one useEffect
  // Use stored pagination state if available, otherwise use defaults
  // When filters change, reset to page 1 but keep the current limit
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.search !== debouncedSearchTerm ||
      prevFiltersRef.current.status !== filterStatus ||
      prevFiltersRef.current.priority !== filterPriority;
    
    // On initial mount, use stored pagination (both page and limit)
    // When filters change, reset to page 1 but keep the stored limit
    // Otherwise, use stored pagination
    const pageToUse = (isInitialMountRef.current || !filtersChanged) 
      ? (pagination.page || DEFAULT_PAGINATION.page)
      : 1;
    const limitToUse = pagination.limit || DEFAULT_PAGINATION.limit;
    
    dispatch(
      fetchOrders({
        page: pageToUse,
        limit: limitToUse,
        search: debouncedSearchTerm,
        status: filterStatus === "all" ? "" : filterStatus,
        priority: filterPriority === "all" ? "" : filterPriority,
      }) as any
    );
    
    // Update previous filter values and mark initial mount as complete
    prevFiltersRef.current = { search: debouncedSearchTerm, status: filterStatus, priority: filterPriority };
    isInitialMountRef.current = false;
  }, [dispatch, debouncedSearchTerm, filterStatus, filterPriority]);

  useEffect(() => {
    console.log("````````````````````", selectedSite);
    console.log("::::::::::selectedOrder:::::::", selectedOrder);
  }, [selectedOrder]);

  useEffect(() => {
    if (!loading && orders) {
      setDisplayData(orders);
      setInitialLoadComplete(true);
    }
  }, [loading, orders]);

  // Restore selection when returning from order detail
  useEffect(() => {
    if (lastViewedOrderId && orders.length > 0 && initialLoadComplete) {
      // Check if the order exists in the current list
      const orderExists = orders.some(order => order.id === lastViewedOrderId);
      if (orderExists) {
        setSelectedOrderId(lastViewedOrderId);
        // Scroll to the selected order after a short delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(`order-${lastViewedOrderId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Clear the last viewed ID after scrolling to avoid re-scrolling on subsequent renders
            dispatch(clearLastViewedOrderId());
          }
        }, 300);
      } else {
        // Order not in current list, clear the selection
        dispatch(clearLastViewedOrderId());
      }
    }
  }, [lastViewedOrderId, orders, initialLoadComplete, dispatch]);

  const handleOrderCreated = (order) => {
    navigate(`/orders/${order.id}`); // Navigate to the new order's detail page
  };

  // const handleCreateOrder = () => {
  //   dispatch(clearSelectedOrder()); // <-- reset Redux order state
  //   setIsCreateModalOpen(true);
  // };

  // No need for client-side filtering since we're using server-side filtering
  const getFilteredOrders = useCallback(() => {
    return displayData;
  }, [displayData]);

  const toggleOrderExpansion = useCallback((orderId: string) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  }, []);

  const toggleProductExpansion = useCallback((productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ClockIcon sx={{ color: "warning.main" }} />;
      case "in_progress":
        return <ClockIcon sx={{ color: "info.main" }} />;
      case "completed":
        return <CheckIcon sx={{ color: "success.main" }} />;
      case "dispatched":
        return <TruckIcon sx={{ color: "secondary.main" }} />;
      default:
        return <ClockIcon sx={{ color: "text.secondary" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "info";
      case "completed":
        return "success";
      case "dispatched":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const showItemQR = (item: Item) => {
    setSelectedItem(item);
  };

  // useEffect(()=>{
  //   selectedOrderId ? setIsCreateModalOpen(true) : null

  // },[selectedOrderId])

  const handleEditOrder = (order: Order) => {
    navigate(`/orders/edit/${order.id}`);
    console.log("==================Order.id========>", order.id);
  };

  const handleCloseEditModal = () => {
    // This function is no longer needed as setEditingOrder is removed
    selectedOrder === null;
    selectedOrderId === null;
    setIsCreateModalOpen(false);
  };

  const handleViewOrder = (orderId: string) => {
    // Store the order ID so we can restore selection when coming back
    dispatch(setLastViewedOrderId(orderId));
    dispatch(fetchOrderById(orderId));
    navigate(`/orders/${orderId}`);
  };

  const handleCustomerClick = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    dispatch(
      fetchOrders({
        page,
        limit: pagination.limit,
        search: searchTerm,
        status: filterStatus === "all" ? "" : filterStatus,
        priority: filterPriority === "all" ? "" : filterPriority,
      }) as any
    );
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    dispatch(
      fetchOrders({
        page: 1, // Reset to first page when changing items per page
        limit: itemsPerPage,
        search: searchTerm,
        status: filterStatus === "all" ? "" : filterStatus,
        priority: filterPriority === "all" ? "" : filterPriority,
      }) as any
    );
  };

  // Hooks must be called before any conditional returns
  const canEdit = usePermission("orders", "update");
  const filteredOrders = getFilteredOrders();

  if (!initialLoadComplete && loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Orders
        </Typography>
        <OrderListShimmer />
      </Box>
    );
  }

  const handleCreateOrder = () => {
    dispatch(clearSelectedOrder()); // <-- reset redux selectedOrder
    navigate("/orders/new");
    setIsCreateModalOpen(true);
  };

  return (
    <PageWrapper>
      {/* Header Section */}
      <HeaderSection
        title="Orders"
        description="Manage manufacturing orders and track production progress"
        icon={
          <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        }
        buttonIcon={<PlusIcon className="h-5 w-5 mr-2" />}
        buttonText="New Order"
        onButtonClick={handleCreateOrder}
      />

      {/* View Mode Toggle */}
      {/* <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant={viewMode === "compact" ? "contained" : "outlined"}
          size="small"
          onClick={() => setViewMode("compact")}
          startIcon={<ViewIcon />}
        >
          Compact
        </Button>
        <Button
          variant={viewMode === "detailed" ? "contained" : "outlined"}
          size="small"
          onClick={() => setViewMode("detailed")}
          startIcon={<TimelineIcon />}
        >
          Detailed
        </Button>
      </Box> */}
      {/* Order List - Compact or Detailed */}
      {viewMode === "compact" ? (
        <CompactOrderList
          orders={filteredOrders}
          loading={loading}
          onEdit={handleEditOrder}
          onView={handleViewOrder}
          onPrint={setQrPrintOrder}
          onRefresh={() =>
            dispatch(
              fetchOrders({
                page: pagination.page || DEFAULT_PAGINATION.page,
                limit: pagination.limit || DEFAULT_PAGINATION.limit,
                search: debouncedSearchTerm,
                status: filterStatus === "all" ? "" : filterStatus,
                priority: filterPriority === "all" ? "" : filterPriority,
              }) as any
            )
          }
          onExport={() => console.log("Export orders")}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterStatus={filterStatus}
          onStatusFilterChange={setFilterStatus}
          filterPriority={filterPriority}
          onPriorityFilterChange={setFilterPriority}
          sortBy={sortBy}
          onSortChange={setSortBy}
          showFilters={true}
          showActions={true}
          compactMode={true}
          selectedOrderId={selectedOrderId}
        />
      ) : (
        <DetailedOrderList
          orders={filteredOrders}
          loading={loading}
          onEdit={handleEditOrder}
          onView={handleViewOrder}
          onPrint={setQrPrintOrder}
          onRefresh={() =>
            dispatch(
              fetchOrders({
                page: pagination.page || DEFAULT_PAGINATION.page,
                limit: pagination.limit || DEFAULT_PAGINATION.limit,
                search: debouncedSearchTerm,
                status: filterStatus === "all" ? "" : filterStatus,
                priority: filterPriority === "all" ? "" : filterPriority,
              }) as any
            )
          }
          onExport={() => console.log("Export orders")}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterStatus={filterStatus}
          onStatusFilterChange={setFilterStatus}
          filterPriority={filterPriority}
          onPriorityFilterChange={setFilterPriority}
          sortBy={sortBy}
          onSortChange={setSortBy}
          showFilters={true}
          showActions={true}
          selectedOrderId={selectedOrderId}
        />
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
        />
      )}

      {/* Modals */}
      {/* <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onOrderCreated={(order) => {
          setQrPrintOrder(order);
          setIsCreateModalOpen(false);
        }}
      /> */}

      {/* <PlaceOrderModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseEditModal}
        siteId={null}
        customerId={null}
      /> */}

      <QRScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
      />

      {qrPrintOrder && (
        <QRCodePrintView
          order={qrPrintOrder}
          onClose={() => setQrPrintOrder(null)}
        />
      )}

      {/* Item QR Code Modal */}
      {selectedItem && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            zIndex: 1300,
          }}
        >
          <Paper sx={{ maxWidth: 400, width: "100%", p: 3 }}>
            <Typography variant="h6" align="center" gutterBottom>
              Item QR Code
            </Typography>
            <Box
              sx={{
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <QRCodeSVG
                value={selectedItem?.qrCode || ""}
                size={200}
                level="M"
                style={{ margin: "0 auto", display: "block" }}
              />
            </Box>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Type:</strong> {selectedItem.type}
              </Typography>
              <Typography variant="body2">
                <strong>Current Stage:</strong> {selectedItem.currentStage}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {selectedItem.status}
              </Typography>
              <Typography variant="body2">
                <strong>QR Code:</strong>{" "}
                <Box
                  component="code"
                  sx={{
                    bgcolor: "background.default",
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.875rem",
                  }}
                >
                  {selectedItem.qrCode}
                </Box>
              </Typography>
            </Stack>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSelectedItem(null)}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Paper>
        </Box>
      )}
    </PageWrapper>
  );
};

export default Orders;
